import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {createPortal} from "react-dom";

import {AnimationGroup} from "@khanacademy/khannotations";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    markMinorConversions,
    type MinorConversionId,
} from "@khan/analytics-learning-time";
import {defaultShapes} from "@khan/component-confetti";
import {mediaQueries} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

import HoverDecorationWrapper from "./hover-decoration-wrapper.tsx";
import Illustration from "./illustration.tsx";
import InView from "./in-view.ts";
import {
    SansSerifAttribution,
    SansSerifBody,
    SansSerifBodySmall,
    SansSerifTitleSmall,
    SerifHeadingMedium,
} from "./typography/typography.ts";

const FullscreenConfetti = React.lazy(
    () => import("@khan/component-confetti/fullscreen-confetti"),
);

type CommonCTAProps = {
    copy: string;
    url: string;
    newWindow?: boolean;
};

type ButtonCTAProps = CommonCTAProps & {
    type: "button";
    conversion?: MinorConversionId;
};

type LinkCTAProps = CommonCTAProps & {
    type: "link";
};

type InfoBlockCTAProps = ButtonCTAProps | LinkCTAProps;

type InfoBlockBrandingProps = {
    kind: "kmap";
    path: string;
    alt: string;
    width: number;
};

type EasterEgg = {
    id: string;
    coords: {
        top: string;
        right: string;
        bottom: string;
        left: string;
    };
    buttonText: string;
    ariaMessage: string;
};

type OwnProps = {
    kind: "InfoBlock";
    title?: string;
    headline: string | React.ReactElement;
    attribution?: string;
    copy: string | React.ReactElement;
    bullets?: Array<string>;
    cta?: InfoBlockCTAProps;
    align?: "left" | "right";
    illustration?: string;
    staticGraphic?: string;
    staticGraphicEasterEgg?: EasterEgg;
    hangQuote?: boolean;
    source?: string;
    branding?: InfoBlockBrandingProps;
};

type Props = OwnProps & WithActionSchedulerProps;

type State = {
    // HACK: right now, the copy finishes loading before the illustration,
    // but we want the illustration to animate first. Therefore, we (sigh)
    // pause the animation until a few seconds after we mount. This won't
    // always work, is terrible, and makes me sad. We should instead have a
    // ready callback on Illustration, or have Illustration be ready
    // immediately.
    ready: boolean;
    easterEgg: boolean;
    easterEggMessage: string;
};

class InfoBlock extends React.Component<Props, State> {
    state: State = {
        ready: false,
        easterEgg: false,
        easterEggMessage: "",
    };

    componentDidMount: () => void = () => {
        this.props.schedule.timeout(() => {
            this.setState({ready: true});
        }, 2000);
    };

    handleEasterEgg: () => void = () => {
        this.setState({
            easterEgg: true,
            easterEggMessage:
                this.props.staticGraphicEasterEgg?.ariaMessage ||
                "Surprise confetti!",
        });
        this.props.schedule.timeout(() => {
            this.setState({
                easterEgg: false,
                easterEggMessage: "",
            });
        }, 5000);
    };

    render(): React.ReactNode {
        const {ready} = this.state;
        const {
            title,
            headline,
            copy,
            attribution,
            bullets,
            cta,
            align,
            illustration,
            staticGraphic,
            staticGraphicEasterEgg,
            hangQuote,
            source,
            branding,
        } = this.props;

        const ariaEasterEgg = React.createRef<HTMLDivElement>();

        return (
            <InView>
                {({pctInView}) => (
                    <View
                        style={[
                            styles.container,
                            align === "right" && styles.containerRight,
                        ]}
                    >
                        {illustration && (
                            <React.Fragment>
                                {
                                    // HACK
                                    //
                                    // On mobile, we use StaticCollageImage, which is a
                                    // <picture><source /></picture>.
                                    //
                                    // On FireFox, when remounting these, they disappear for a
                                    // moment, causing reflow.
                                    //
                                    // So, we want to avoid out remounting hack on FireFox
                                    // mobile. To do this, we have two views below. One for
                                    // desktop, and one for mobile.
                                }
                                <AnimationGroup
                                    paused={pctInView < 0.8 || !ready}
                                    // Reset the component when we go in and out of view.
                                    // This makes it so that the animation replays if you
                                    // scroll back up to previous module.
                                    key={
                                        pctInView > 0.0
                                            ? "illustration-in-view"
                                            : "illustration-out-of-view"
                                    }
                                >
                                    <View
                                        style={[
                                            styles.illustration,
                                            styles.illustrationDesktop,
                                        ]}
                                    >
                                        <Illustration name={illustration} />
                                    </View>
                                </AnimationGroup>
                                <View
                                    style={[
                                        styles.illustration,
                                        styles.illustrationMobile,
                                    ]}
                                >
                                    <Illustration name={illustration} />
                                </View>
                            </React.Fragment>
                        )}
                        {!illustration && staticGraphic && (
                            <View
                                style={[
                                    styles.staticGraphic,
                                    align === "right" && styles.imgLeft,
                                ]}
                            >
                                {staticGraphicEasterEgg && (
                                    <React.Fragment>
                                        <img
                                            className={css(styles.img)}
                                            alt=""
                                            src={staticUrl(staticGraphic)}
                                            useMap={
                                                "#" + staticGraphicEasterEgg.id
                                            }
                                        />

                                        <button
                                            className={css(
                                                styles.staticGraphicAction,
                                            )}
                                            style={
                                                staticGraphicEasterEgg.coords
                                            }
                                            aria-label={
                                                staticGraphicEasterEgg.buttonText
                                            }
                                            /* TODO: Update to use Announcer WB-1766 */
                                            onClick={() =>
                                                this.handleEasterEgg()
                                            }
                                        />
                                        <div
                                            className="sr-only"
                                            role="status"
                                            ref={ariaEasterEgg}
                                        >
                                            {this.state.easterEggMessage}
                                        </div>
                                    </React.Fragment>
                                )}
                                {this.state.easterEgg &&
                                    createPortal(
                                        <View style={styles.confettiWrapper}>
                                            <React.Suspense fallback={null}>
                                                <FullscreenConfetti
                                                    numParticles={
                                                        (window.innerWidth *
                                                            window.innerHeight) /
                                                        8500
                                                    }
                                                    emitDuration={3000}
                                                    shapes={defaultShapes}
                                                    spin={10}
                                                    twist={0}
                                                />
                                            </React.Suspense>
                                        </View>,
                                        document.body,
                                    )}
                                {!staticGraphicEasterEgg && (
                                    <img
                                        className={css(styles.img)}
                                        alt=""
                                        src={staticUrl(staticGraphic)}
                                    />
                                )}
                            </View>
                        )}
                        <Strut size={12} />
                        <AnimationGroup paused={pctInView < 0.8}>
                            <View style={styles.content}>
                                {title && (
                                    <SansSerifTitleSmall style={styles.title}>
                                        {title}
                                    </SansSerifTitleSmall>
                                )}
                                <SerifHeadingMedium
                                    style={hangQuote && styles.hangQuote}
                                    tag="h2"
                                >
                                    {headline}
                                </SerifHeadingMedium>
                                {attribution && (
                                    <SansSerifAttribution style={styles.attrib}>
                                        {attribution}
                                    </SansSerifAttribution>
                                )}
                                <SansSerifBody
                                    tag="p"
                                    style={[
                                        styles.copy,
                                        !bullets &&
                                            !source &&
                                            styles.withSpaceBelow,
                                    ]}
                                >
                                    {copy}
                                </SansSerifBody>
                                {bullets && (
                                    <ul className={css(styles.bullets)}>
                                        {bullets.map((bullet, i) => (
                                            <li key={i}>
                                                <SansSerifBodySmall
                                                    style={styles.bullet}
                                                >
                                                    {bullet}
                                                </SansSerifBodySmall>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {source && (
                                    <SansSerifAttribution style={styles.source}>
                                        {source}
                                    </SansSerifAttribution>
                                )}
                                {cta && cta.type === "button" && (
                                    <React.Fragment>
                                        <View
                                            style={styles.ctaButtonContainerBig}
                                        >
                                            <HoverDecorationWrapper>
                                                <Button
                                                    size="large"
                                                    {...(cta.newWindow
                                                        ? {target: "_blank"}
                                                        : null)}
                                                    href={cta.url}
                                                    safeWithNav={async () => {
                                                        if (cta.conversion) {
                                                            await markMinorConversions(
                                                                [
                                                                    {
                                                                        id: cta.conversion,
                                                                    },
                                                                ],
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {cta.copy}
                                                </Button>
                                            </HoverDecorationWrapper>
                                            <Spring />
                                        </View>
                                        <View
                                            style={
                                                styles.ctaButtonContainerSmall
                                            }
                                        >
                                            <Button
                                                {...(cta.newWindow
                                                    ? {target: "_blank"}
                                                    : null)}
                                                href={cta.url}
                                                safeWithNav={async () => {
                                                    if (cta.conversion) {
                                                        await markMinorConversions(
                                                            [
                                                                {
                                                                    id: cta.conversion,
                                                                },
                                                            ],
                                                        );
                                                    }
                                                }}
                                            >
                                                {cta.copy}
                                            </Button>
                                        </View>
                                    </React.Fragment>
                                )}
                                {cta && cta.type === "link" && (
                                    <Link
                                        {...(cta.newWindow
                                            ? {target: "_blank"}
                                            : null)}
                                        href={cta.url}
                                        style={styles.link}
                                    >
                                        {cta.copy + " ›"}
                                    </Link>
                                )}
                                {branding && (
                                    <View style={styles.brandingImageContainer}>
                                        <div>
                                            <img
                                                className={css(
                                                    styles.brandingImage,
                                                )}
                                                style={{width: branding.width}}
                                                alt={branding.alt}
                                                src={staticUrl(branding.path)}
                                            />
                                        </div>
                                    </View>
                                )}
                            </View>
                        </AnimationGroup>
                    </View>
                )}
            </InView>
        );
    }
}

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof InfoBlock,
        React.ComponentProps<typeof InfoBlock>
    >
>;
export default withActionScheduler(
    InfoBlock,
) as React.ComponentType<ExportProps>;

const fadeBackground = {
    "0%": {
        background: "rgba(0, 0, 0, 0)",
    },
    "100%": {
        background: "rgba(26, 26, 26, 0.3)",
    },
} as const;

const styles = StyleSheet.create({
    title: {
        color: "rgba(33, 36, 44, 0.64)",
        margin: "0 0 37px 0",
    },
    container: {
        alignItems: "center",
        // HACK (sort of?)
        // We want the illustation to animate first, and things animate in
        // DOM order, so we reverse the direction.
        flexDirection: "row-reverse",
        maxWidth: 1200,
        padding: "0 20px",
        margin: "0 auto 200px auto",
        [mediaQueries.mdOrSmaller]: {
            flexDirection: "column",
            maxWidth: "inherit",
            paddingLeft: 20,
            paddingRight: 20,
        },
        [mediaQueries.smOrSmaller]: {
            marginBottom: 165,
        },
    },
    containerRight: {
        flexDirection: "row",
    },
    confettiWrapper: {
        position: "fixed",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        transform: "translate3d(0, 0, 0)",
        // @ts-expect-error - TS2322 - Type '{ readonly "0%": { readonly background: "rgba(0, 0, 0, 0)"; }; readonly "100%": { readonly background: "rgba(26, 26, 26, 0.3)"; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: fadeBackground,
        animationDuration: "4s",
        animationFillMode: "forwards",
        transition: "opacity 4s ease-in-out",
        opacity: 1.0,
    },
    content: {
        flexBasis: 464,
        flexShrink: 0,
        margin: 0,
        [mediaQueries.mdOrSmaller]: {
            width: 548,
            marginLeft: "auto",
            marginRight: "auto",
            flexBasis: "auto",
        },
        [mediaQueries.smOrSmaller]: {
            maxWidth: 584,
            width: "auto",
        },
    },
    link: {
        color: color.offBlack,
        fontSize: 16,
        fontWeight: "bold",
        textDecoration: "none",
        ":hover": {
            textDecoration: "underline",
        },
    },
    ctaButtonContainerBig: {
        flexDirection: "row",
        [mediaQueries.smOrSmaller]: {
            display: "none",
            width: "100%",
        },
    },
    ctaButtonContainerSmall: {
        flexDirection: "row",
        width: "100%",
        display: "none",
        [mediaQueries.smOrSmaller]: {
            display: "flex",
        },
    },
    attrib: {
        color: "rgba(33, 36, 44, 0.64)",
        margin: "14px 0 0 0",
    },
    source: {
        color: "rgba(33, 36, 44, 0.64)",
        margin: "0 0 42px 0",
    },
    copy: {
        margin: "26px 0 8px 0",
    },
    withSpaceBelow: {
        margin: "26px 0 42px 0",
    },
    illustrationDesktop: {
        [mediaQueries.mdOrSmaller]: {
            display: "none",
        },
    },
    illustrationMobile: {
        height: "auto",
        marginBottom: 50,
        [mediaQueries.smOrSmaller]: {
            marginBottom: 30,
        },
        [mediaQueries.lgOrLarger]: {
            display: "none",
        },
    },
    illustration: {
        [mediaQueries.lgOrLarger]: {
            flex: 1,
            alignSelf: "stretch",
        },
        [mediaQueries.mdOrSmaller]: {
            width: "100%",
        },
    },
    staticGraphic: {
        position: "relative",
        padding: "0 0 0 0",
        margin: "0 0 0 90px",
        [mediaQueries.mdOrSmaller]: {
            margin: "0 110px",
        },
        [mediaQueries.smOrSmaller]: {
            margin: "0 24px 24px 24px",
        },
    },
    staticGraphicAction: {
        position: "absolute",
        background: "transparent",
        border: "none",
        cursor: "pointer",
    },
    imgLeft: {
        margin: "0 90px 0 0",
    },
    img: {
        width: "100%",
        height: "auto",
    },
    hangQuote: {
        textIndent: "-0.35em",
    },
    bullets: {
        listStyleType: "initial",
        paddingLeft: 16,
        // Design specifies that the bullet points be 82% opacity.
        opacity: 0.82,
    },
    bullet: {
        paddingLeft: 5,
    },
    brandingImageContainer: {
        margin: "48px 0 0 0",

        [mediaQueries.smOrSmaller]: {
            marginTop: 40,
        },
    },
    brandingImage: {
        height: "auto",
        maxWidth: "100%",
    },
});
